import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import DangerZone from '../components/DangerZone.jsx';
import { Link } from 'react-router-dom';
import QRCode from 'react-qr-code';

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [prefs, setPrefs] = useState({ channel: 'EMAIL', email: '', phone: '' });
  const [name, setName] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getMe().then((p) => {
      setProfile(p);
      setName(p.displayName ?? '');
      setPrefs({
        channel: p.notificationPrefs?.channel ?? 'EMAIL',
        email: p.notificationPrefs?.email ?? p.email ?? '',
        phone: p.notificationPrefs?.phone ?? '',
      });
    }).catch((e) => setErr(e.message));
  }, []);

  async function save(e) {
    e.preventDefault();
    setMsg(null); setErr(null); setBusy(true);
    try {
      const body = {
        displayName: name,
        notificationPrefs: {
          channel: prefs.channel,
          email: prefs.email || undefined,
          phone: prefs.phone || undefined,
        },
      };
      await api.updateMe(body);
      setMsg('Saved.');
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  if (!profile) return <div className="muted center loading-block">Loading…</div>;

  return (
    <div className="container-narrow page">
      <span className="eyebrow">Account</span>
      <h2 className="mt-8 mb-24">Settings</h2>

      <div className="card">
        <form onSubmit={save}>
          {msg && <div className="alert success mb-18">{msg}</div>}
          {err && <div className="alert error mb-18">{err}</div>}

          <div className="field">
            <label>Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          </div>

          <div className="field">
            <label>Signed in as</label>
            <input value={profile.email} disabled />
          </div>

          <h4 className="mt-28 mb-12">How should we reach you when an item is found?</h4>

          <div className="field">
            <label>Channel</label>
            <select value={prefs.channel} onChange={(e) => setPrefs({ ...prefs, channel: e.target.value })}>
              <option value="EMAIL">Email only</option>
              <option value="SMS">Text message only</option>
              <option value="BOTH">Both email and text</option>
            </select>
          </div>

          {(prefs.channel === 'EMAIL' || prefs.channel === 'BOTH') && (
            <div className="field">
              <label>Notification email</label>
              <input type="email" value={prefs.email} onChange={(e) => setPrefs({ ...prefs, email: e.target.value })} />
            </div>
          )}

          {(prefs.channel === 'SMS' || prefs.channel === 'BOTH') && (
            <div className="field">
              <label>Phone (E.164 format)</label>
              <input value={prefs.phone} onChange={(e) => setPrefs({ ...prefs, phone: e.target.value })} placeholder="+14155551234" />
              <div className="hint">Country code + number, no spaces. e.g. +14155551234</div>
            </div>
          )}

          <button className="btn btn-primary btn-block mt-12" disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      </div>

      <PersonalCode profile={profile} />
      <DangerZone />
    </div>
  );
}

function PersonalCode({ profile }) {
  const handle = profile.publicHandle;
  const [copied, setCopied] = useState(false);
  if (!handle) {
    return (
      <div className="card mt-24">
        <span className="eyebrow">Your code</span>
        <h4 className="mt-8">Not ready yet</h4>
        <p className="muted">
          Save your settings once to generate your personal found-item code.
        </p>
      </div>
    );
  }
  // Use the short-URL subdomain for the QR. The CloudFront Function on
  // e.ezbounty.com 302-redirects to the canonical found.html URL on the
  // apex, but the short form keeps the QR code small (drops it from QR
  // Version 3 to Version 2 — meaningfully larger modules at any sticker size).
  const shortUrl = import.meta.env.VITE_SHORT_URL ?? 'https://e.ezbounty.com';
  const foundUrl = `${shortUrl}/${handle}`;

  const copy = () => {
  navigator.clipboard?.writeText(foundUrl);
  setCopied(true);
  setTimeout(() => setCopied(false), 2000);
};

  return (
    <div className="card mt-24">
      <span className="eyebrow">Your code</span>
      <h4 className="mt-8 mb-8">One sticker for everything you own</h4>
      <p className="muted mt-0">
        Print this once and stick it on your keychain, phone, wallet, or laptop. Anyone who
        finds a lost item of yours can scan it to send you a report — no per-item tagging needed.
      </p>

      <div className="center qr-block">
        <div className="qr-img">
          <QRCode value={foundUrl} size={256} level="M" title="Your personal QR code" />
        </div>
      </div>

      <div className="field">
        <label>Your found-item link</label>
        <input value={foundUrl} readOnly onFocus={(e) => e.target.select()} />
      </div>

      <div className="row mt-12">
        <button type="button" className="btn btn-ghost" onClick={copy}>
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        <Link to="/settings/handle/print" className="btn btn-ghost">
          Print labels
        </Link>
      </div>
    </div>
  );
}
